/**
 * Cover 4 Palms — the corner and the safety, as responsibility specs.
 *
 * Palms is a two-man conversation. If #2 breaks out shallow and early, the CORNER comes off #1
 * and jumps #2 to the flat, and the SAFETY takes #1 over the top. See `.kiro/steering/cover4Palms.md`.
 *
 * ## The "Out" call is DERIVED, and it is derived PER TICK
 *
 * Neither defender is told the call. Both watch #2 and each reaches his own conclusion — the
 * model `.kiro/steering/reaction-engine.md` mandates (no shared state, no call flag).
 *
 * Critically, the call is detected the tick #2's break is OBSERVED, not by scanning the route in
 * advance. The hand-written policies these replace pre-scanned the whole feed with
 * `classifyPalmsEarlyOut` and then acted from the snap, which meant both defenders repositioned
 * before the out had happened — they knew the future. That is the one thing the engine's core
 * principle forbids, and it was visible in the app as the corner jumping #2 instantly.
 *
 * Now each defender plays his normal read until he sees the break, then reacts (plus his
 * reaction latency, which the interpreter applies for free). Arriving late is the point.
 */

import { PalmsCornerParams, ManMatchParams } from '../../types';
import { KeyObservables, isHardBreak, isBreakingToward } from '../observables';
import { ReadContext, ReadAction, ResponsibilitySpec } from '../responsibility';
import { MAN_MATCH_SPEC } from './manMatchSpec';

/**
 * Is this receiver making the shallow, early out-break that triggers the "Out" call?
 *
 * Purely observational: a hard cut, breaking toward this side's sideline, while still above the
 * shallow depth line. A deep out looks the same laterally but happens past `out2DepthY`, which
 * is why the depth test is what separates the two.
 */
export function isPalmsEarlyOut(obs: KeyObservables, params: PalmsCornerParams): boolean {
  return (
    isHardBreak(obs, params.breakAngleDeg) &&
    isBreakingToward(obs, params.out2SignX, params.out2LateralEps) &&
    obs.position.y > params.out2DepthY
  );
}

/**
 * Re-type a Palms context as a man-match one so the underlying MOD read can be reused wholesale
 * rather than duplicated. Both Palms defenders play a normal man-match until the call fires.
 */
const asManMatch = (ctx: ReadContext<PalmsCornerParams>): ReadContext<ManMatchParams> => ({
  ...ctx,
  params: ctx.params.manMatch,
});

/**
 * The MOD transition rules, lifted onto Palms params and GATED on the call not having fired.
 *
 * The gate matters: once a Palms defender has committed on the "Out" call, he is locked to that
 * receiver. Without it the underlying man-match rules keep evaluating against his original key
 * and can yank him back off — e.g. the corner jumps #2, then #1's shallow break trips the
 * pass-off rule and drags him into a deep-third drop he has no business taking.
 */
const modTransitionsGatedOn = (binding: string) =>
  MAN_MATCH_SPEC.transitions.map((t) => ({
    ...t,
    when: (ctx: ReadContext<PalmsCornerParams>) =>
      ctx.bindings[binding] === null && t.when(asManMatch(ctx)),
  }));

/** The MOD latches, lifted the same way. */
const modLatches = Object.fromEntries(
  Object.entries(MAN_MATCH_SPEC.latches ?? {}).map(([name, pred]) => [
    name,
    (ctx: ReadContext<PalmsCornerParams>) => pred(asManMatch(ctx)),
  ])
);

/** Trail a receiver from underneath — how a corner plays a route he has jumped in the flat. */
const jumpUnderneath = (target: KeyObservables, params: PalmsCornerParams): ReadAction => ({
  target: { x: target.position.x, y: target.position.y + params.trailCushion },
  speed: params.maxSpeed,
});

/** Carry a receiver from over the top — never moving shallower than current depth. */
const carryOverTop = (
  target: KeyObservables,
  params: PalmsCornerParams,
  defenderY: number
): ReadAction => ({
  target: {
    x: target.position.x,
    y: Math.min(defenderY, target.position.y - params.trailCushion),
  },
  speed: params.maxSpeed,
});

// ============================================
// Corner — MOD on #1 until the "Out" call, then jump #2
// ============================================

export const PALMS_CORNER_SPEC: ResponsibilitySpec<PalmsCornerParams> = {
  id: 'palms-corner',
  initialTechnique: 'backpedal',

  latches: {
    // Declared FIRST so `outCall` can consult it in the same pass — the interpreter evaluates
    // latches in declaration order. Once #2 has gotten vertical past the shallow line, a later
    // out-break is a DEEP out and no longer triggers the call.
    key2Deep: ({ partners, params }) =>
      partners[0] !== undefined && partners[0].position.y <= params.out2DepthY,
    outCall: ({ partners, params, latches }) =>
      !latches.key2Deep && partners[0] !== undefined && isPalmsEarlyOut(partners[0], params),
    ...modLatches,
  },

  bindings: {
    /** Which receiver he has come off to jump. Binds #2 (partner 0) when the call fires. */
    jumped: ({ latches }) => (latches.outCall ? 0 : null),
  },

  transitions: [
    {
      // Fires from READ or ZONE only. If he is ALREADY in MAN (carrying #1 vertical) when the
      // call comes, no state change is needed — the `jumped` binding alone redirects the MAN
      // behavior onto #2. Note this rule must NOT test `bindings.jumped`: bindings resolve
      // before transitions, so it is already set on the very tick the latch fires.
      note: 'Out call — come off #1 and jump #2 in the flat',
      from: ['READ', 'ZONE'],
      to: 'MAN',
      when: ({ latches }) => latches.outCall,
    },
    ...modTransitionsGatedOn('jumped'),
  ],

  behavior: {
    READ: (ctx) => MAN_MATCH_SPEC.behavior.READ(asManMatch(ctx)),
    ZONE: (ctx) => MAN_MATCH_SPEC.behavior.ZONE(asManMatch(ctx)),
    MAN: (ctx) => {
      const jumped = ctx.bound('jumped');
      // Jumped #2: squat and drive on the out. Otherwise this is the ordinary MOD carry on #1.
      return jumped
        ? jumpUnderneath(jumped, ctx.params)
        : MAN_MATCH_SPEC.behavior.MAN(asManMatch(ctx));
    },
  },
};

// ============================================
// Safety — robber read on #2 until the "Out" call, then take #1 over the top
// ============================================

export const PALMS_SAFETY_SPEC: ResponsibilitySpec<PalmsCornerParams> = {
  id: 'palms-safety',
  initialTechnique: 'backpedal',

  latches: {
    // The safety's own KEY is #2, so he reads the call off his primary rather than a partner.
    key2Deep: ({ key, params }) => key.position.y <= params.out2DepthY,
    outCall: ({ key, params, latches }) => !latches.key2Deep && isPalmsEarlyOut(key, params),
    ...modLatches,
  },

  bindings: {
    /** #1 (partner 0), whom he takes over the top once the corner has come off to jump #2. */
    takeOver: ({ latches, partners }) => (latches.outCall && partners[0] !== undefined ? 0 : null),
  },

  transitions: [
    {
      note: 'Out call — the corner has #2, so take #1 over the top',
      from: ['READ', 'ZONE'],
      to: 'MAN',
      when: ({ latches }) => latches.outCall,
    },
    ...modTransitionsGatedOn('takeOver'),
  ],

  behavior: {
    READ: (ctx) => MAN_MATCH_SPEC.behavior.READ(asManMatch(ctx)),
    ZONE: (ctx) => MAN_MATCH_SPEC.behavior.ZONE(asManMatch(ctx)),
    MAN: (ctx) => {
      const taken = ctx.bound('takeOver');
      // Took #1 on the call: mirror him laterally, hold depth over the top. Otherwise this is
      // the ordinary robber read carrying #2.
      return taken
        ? carryOverTop(taken, ctx.params, ctx.defender.y)
        : MAN_MATCH_SPEC.behavior.MAN(asManMatch(ctx));
    },
  },
};
